import { useRef, useState } from "react";
import { Button, Descriptions, InputNumber, Spin, Tag, Upload, message } from "antd";
import axios from "axios";
import Url from "../../apiCall/UrlConfig";

// تصویر برای نمایش کوچک می‌شود ولی مختصات باید در فضای پیکسل تصویر اصلی به
// سرور برود، وگرنه مقیاس میلیمتری غلط از آب درمی‌آید.
const MAX_DISPLAY_WIDTH = 640;

const PublicCbct = () => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  const [display, setDisplay] = useState({ width: 0, height: 0 });
  const [points, setPoints] = useState([]);
  const [realSizeMm, setRealSizeMm] = useState(10);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const imgRef = useRef(null);

  const _reset = () => {
    setPoints([]);
    setResult(null);
  };

  const _onSelectFile = (selected) => {
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    _reset();
    return false; // جلوی آپلود خودکار antd را می‌گیرد
  };

  const _onImageLoad = (event) => {
    const el = event.target;
    const scale = Math.min(1, MAX_DISPLAY_WIDTH / el.naturalWidth);
    setNatural({ width: el.naturalWidth, height: el.naturalHeight });
    setDisplay({
      width: Math.round(el.naturalWidth * scale),
      height: Math.round(el.naturalHeight * scale),
    });
  };

  const _onClickImage = (event) => {
    if (points.length >= 2) return;
    const rect = imgRef.current.getBoundingClientRect();
    setPoints([
      ...points,
      {
        // مختصات نمایشی برای رسم نشانگر
        left: event.clientX - rect.left,
        top: event.clientY - rect.top,
        // و همان نقطه در فضای پیکسل تصویر اصلی برای ارسال
        x: ((event.clientX - rect.left) / rect.width) * natural.width,
        y: ((event.clientY - rect.top) / rect.height) * natural.height,
      },
    ]);
  };

  const _analyze = async () => {
    if (file == null) {
      message.warning("اول یک تصویر CBCT انتخاب کنید");
      return;
    }
    if (points.length < 2) {
      message.warning("دو نقطه با فاصلهٔ مشخص را روی تصویر علامت بزنید");
      return;
    }

    const formData = new FormData();
    formData.append("Image", file);
    formData.append("X1", points[0].x);
    formData.append("Y1", points[0].y);
    formData.append("X2", points[1].x);
    formData.append("Y2", points[1].y);
    formData.append("RealSizeMm", realSizeMm);

    try {
      setLoading(true);
      setResult(null);
      const res = await axios.post(
        Url.baseURL[0] + Url.publicCbctImplantLine,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const data = res?.data?.traceMessage;
      if (data?.ok !== true) {
        message.error(
          data?.reason === "no_segment"
            ? "استخوانی در تصویر تشخیص داده نشد"
            : "تحلیل انجام نشد"
        );
        setResult(data ?? null);
        return;
      }
      setResult(data);
    } catch (error) {
      const status = error?.response?.status;
      message.error(
        status === 429
          ? "تعداد درخواست زیاد است، چند دقیقه دیگر تلاش کنید"
          : error?.response?.data?.message || "خطا در تحلیل تصویر"
      );
    } finally {
      setLoading(false);
    }
  };

  const _rows = () => {
    const mm = (v) => (v == null ? "—" : `${v} mm`);
    return [
      { key: "len", label: "طول ایمپلنت", children: mm(result.implantLenMm) },
      { key: "crest", label: "کرست تا سینوس", children: mm(result.crestToSinusMm) },
      { key: "w1", label: "عرض استخوان در ۱ میلی‌متری", children: mm(result.widthAt1Mm) },
      { key: "w3", label: "عرض استخوان در ۳ میلی‌متری", children: mm(result.widthAt3Mm) },
      { key: "w6", label: "عرض استخوان در ۶ میلی‌متری", children: mm(result.widthAt6Mm) },
      { key: "angle", label: "زاویهٔ محور", children: result.angleDeg == null ? "—" : `${result.angleDeg}°` },
      { key: "area", label: "مساحت مقطع", children: result.areaMm2 == null ? "—" : `${result.areaMm2} mm²` },
      {
        key: "sinus",
        label: "سینوس",
        children: result.sinusFound ? <Tag color="green">پیدا شد</Tag> : <Tag color="orange">پیدا نشد</Tag>,
      },
    ];
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }} dir="rtl">
      <h2>تحلیل CBCT — اندازه‌گیری محور ایمپلنت</h2>
      <p style={{ color: "#666" }}>
        یک تصویر CBCT انتخاب کنید، سپس دو نقطه‌ای را که فاصلهٔ واقعی‌شان را
        می‌دانید روی تصویر علامت بزنید. تصویر شما ذخیره نمی‌شود.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <Upload beforeUpload={_onSelectFile} showUploadList={false} accept="image/*" maxCount={1}>
          <Button>انتخاب تصویر CBCT</Button>
        </Upload>

        <span>
          فاصلهٔ واقعی دو نقطه:{" "}
          <InputNumber min={1} max={200} step={1} value={realSizeMm} onChange={setRealSizeMm} /> میلی‌متر
        </span>

        <Button onClick={_reset} disabled={points.length === 0 && result == null}>
          پاک کردن نقاط
        </Button>

        <Button type="primary" loading={loading} onClick={_analyze} disabled={file == null}>
          تحلیل
        </Button>

        <Tag color={points.length === 2 ? "green" : "orange"}>{points.length} / 2 نقطه</Tag>
      </div>

      {previewUrl && (
        <div
          style={{
            position: "relative",
            width: display.width || "auto",
            height: display.height || "auto",
            cursor: points.length < 2 ? "crosshair" : "default",
            display: "inline-block",
          }}
        >
          <img
            ref={imgRef}
            src={previewUrl}
            alt="cbct"
            onLoad={_onImageLoad}
            onClick={_onClickImage}
            style={{ width: display.width || "auto", display: "block" }}
            draggable={false}
          />

          {points.map((p, index) => (
            <div
              key={index}
              style={{
                position: "absolute",
                left: p.left - 6,
                top: p.top - 6,
                width: 12,
                height: 12,
                borderRadius: 12,
                backgroundColor: "red",
                border: "2px solid white",
                pointerEvents: "none",
              }}
            />
          ))}

          {points.length === 2 && (
            <svg
              style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", pointerEvents: "none" }}
            >
              <line
                x1={points[0].left}
                y1={points[0].top}
                x2={points[1].left}
                y2={points[1].top}
                stroke="red"
                strokeWidth="2"
              />
            </svg>
          )}
        </div>
      )}

      {loading && (
        <div style={{ marginTop: 24 }}>
          <Spin /> در حال تحلیل…
        </div>
      )}

      {result?.ok && (
        <div style={{ marginTop: 24 }}>
          <Descriptions title="نتیجه" bordered column={{ xs: 1, sm: 2 }} size="small">
            {_rows().map((row) => (
              <Descriptions.Item key={row.key} label={row.label}>
                {row.children}
              </Descriptions.Item>
            ))}
          </Descriptions>

          {result.resultImage && (
            <div style={{ marginTop: 16, overflowX: "auto" }}>
              <img
                src={`data:image/png;base64,${result.resultImage}`}
                alt="نتیجه تحلیل"
                style={{ maxWidth: "100%" }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicCbct;
